import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lightbulb, Sparkles, Image as ImageIcon, Paperclip, CheckSquare, MessageSquare, RefreshCw, Save, Loader2, AlertTriangle, ArrowDownUp } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty, Avatar } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
const isImg = (a) => (a.content_type || "").startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(a.url || "");

const STATUS_META = {
  eingegangen: { label: "Eingegangen", cls: "bg-sky-400/15 text-sky-300 border-sky-400/30" },
  neu: { label: "Neu", cls: "bg-white/10 text-gray-300 border-white/10" },
  in_pruefung: { label: "In Prüfung", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  umgewandelt: { label: "Umgewandelt", cls: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30" },
  erledigt: { label: "Erledigt", cls: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};
const PRIO_CLS = { hoch: "text-red-300", mittel: "text-amber-300", niedrig: "text-gray-400" };
const FILTERS = [
  { key: "", label: "Alle" }, { key: "eingegangen", label: "Eingegangen" }, { key: "neu", label: "Neu" }, { key: "in_pruefung", label: "In Prüfung" },
  { key: "umgewandelt", label: "Umgewandelt" }, { key: "abgelehnt", label: "Abgelehnt" },
];

function Tile({ label, value, icon: Icon, tone }) {
  const red = tone === "red" && value > 0;
  return (
    <div className={`rounded-xl p-4 border ${red ? "bg-red-500/[0.07] border-red-500/30" : "bg-[#121217] border-white/10"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${red ? "text-red-400" : "text-[#A3E635]"}`} />}
      </div>
      <div className={`mt-2 text-3xl font-black tracking-tight ${red ? "text-red-300" : ""}`}>{value}</div>
    </div>
  );
}

function RequestRow({ r, onChanged }) {
  const { t } = useI18n();
  const [note, setNote] = useState(r.admin_note || "");
  const [busy, setBusy] = useState("");
  const sm = STATUS_META[r.status] || STATUS_META.neu;
  const images = (r.attachments || []).filter(isImg);
  const files = (r.attachments || []).filter((a) => !isImg(a));

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(t(okMsg)); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(""); }
  };
  const setStatus = (status) => act(`status-${status}`, () => api.put(`/admin/ai-requests/${r.id}`, { status }), "Status aktualisiert");
  const saveNote = () => act("note", () => api.put(`/admin/ai-requests/${r.id}`, { admin_note: note }), "Notiz gespeichert");
  const analyze = () => act("analyze", () => api.post(`/admin/ai-requests/${r.id}/analyze`), "KI-Auswertung aktualisiert");
  const toTask = () => act("task", () => api.post(`/admin/ai-requests/${r.id}/to-task`), "Aufgabe erstellt");
  const toSupport = () => act("support", () => api.post(`/admin/ai-requests/${r.id}/to-support`), "Supportfall erstellt");
  const toDesign = () => act("design", () => api.post(`/admin/ai-requests/${r.id}/to-design`), "Design-Idee erstellt");

  return (
    <Card className={`p-5 ${r.overdue ? "border-red-500/40" : ""}`} data-testid={`airequest-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={r.company_name} color={r.primary_color || "#A3E635"} size={38} />
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{r.card?.subject || t("Wunsch")}</div>
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">
              {r.company_name} · {r.request_no} · {r.card?.category_label}
              {r.card?.priority && <> · <span className={PRIO_CLS[r.card.priority]}>{r.card.priority_label}</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {r.overdue && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`airequest-overdue-${r.id}`} title={t("Offen seit über 7 Tagen ohne Bearbeitung")}>
              <AlertTriangle className="w-3 h-3" /> {t("Überfällig")}
            </span>
          )}
          {r.ai_analyzed && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#A3E635]/15 text-[#A3E635] border border-[#A3E635]/30"><Sparkles className="w-3 h-3" /> {t("KI")}</span>}
          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${sm.cls}`}>{t(sm.label)}</span>
        </div>
      </div>

      {r.text && (
        <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Originaltext des Kunden")}</div>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{r.text}</p>
        </div>
      )}

      {(r.card?.summary || r.card?.image_findings || r.card?.next_step) && (
        <div className="mt-3 rounded-lg p-3 border" style={{ borderColor: "#A3E63533", background: "#A3E6350D" }}>
          <div className="text-[10px] uppercase tracking-wider mb-1 text-[#A3E635] font-bold">{t("Wunschkarte")}</div>
          {r.card?.summary && <p className="text-sm text-gray-200 whitespace-pre-wrap">{r.card.summary}</p>}
          {r.card?.image_findings && <p className="text-xs text-gray-400 mt-2 flex items-start gap-1.5"><ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#A3E635]" /> {r.card.image_findings}</p>}
          {r.card?.next_step && <p className="text-xs text-gray-400 mt-1.5"><span className="font-semibold text-gray-300">{t("Nächster Schritt")}:</span> {r.card.next_step}</p>}
        </div>
      )}
      {r.ai_error && <p className="text-xs text-amber-300 mt-2">{r.ai_error}</p>}

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {images.map((a, i) => (
            <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid={`airequest-img-${r.id}-${i}`}
              className="w-20 h-20 rounded-lg overflow-hidden border border-white/10 bg-white/5">
              <img src={fileUrl(a.url)} alt={a.label || ""} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((a, i) => (
            <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-gray-300 hover:bg-white/10">
              <Paperclip className="w-3 h-3" /> <span className="max-w-[160px] truncate">{a.label || t("Anhang")}</span>
            </a>
          ))}
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Interne Notiz (nur KEWAG)")}</div>
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`airequest-note-${r.id}`}
            placeholder={t("Notiz für das Team …")}
            className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]" />
          <button onClick={saveNote} disabled={busy === "note"} data-testid={`airequest-note-save-${r.id}`}
            className="px-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 disabled:opacity-50 flex items-center gap-1.5 text-sm">
            {busy === "note" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {!r.ai_analyzed && (
          <button onClick={analyze} disabled={busy === "analyze"} data-testid={`airequest-analyze-${r.id}`}
            className="text-xs font-bold rounded-lg px-3 py-2 flex items-center gap-1.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 disabled:opacity-50">
            {busy === "analyze" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("KI-Auswertung starten")}
          </button>
        )}
        <button onClick={toTask} disabled={busy === "task"} data-testid={`airequest-to-task-${r.id}`}
          className="text-xs font-bold rounded-lg px-3 py-2 flex items-center gap-1.5 text-black bg-[#A3E635] hover:brightness-105 disabled:opacity-50">
          {busy === "task" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />} {t("In Aufgabe umwandeln")}
        </button>
        <button onClick={toSupport} disabled={busy === "support"} data-testid={`airequest-to-support-${r.id}`}
          className="text-xs font-bold rounded-lg px-3 py-2 flex items-center gap-1.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 disabled:opacity-50">
          {busy === "support" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} {t("In Supportfall umwandeln")}
        </button>
        <button onClick={toDesign} disabled={busy === "design"} data-testid={`airequest-to-design-${r.id}`}
          className="text-xs font-bold rounded-lg px-3 py-2 flex items-center gap-1.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 disabled:opacity-50">
          {busy === "design" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />} {t("In Design-Idee umwandeln")}
        </button>
        <div className="flex-1" />
        {["in_pruefung", "erledigt", "abgelehnt"].filter((s) => s !== r.status).map((s) => (
          <button key={s} onClick={() => setStatus(s)} disabled={busy === `status-${s}`} data-testid={`airequest-status-${s}-${r.id}`}
            className="text-[11px] font-semibold rounded-lg px-2.5 py-1.5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50">
            → {t(STATUS_META[s].label)}
          </button>
        ))}
      </div>

      {(r.converted_task_id || r.converted_ticket_id || r.converted_design_id) && (
        <div className="mt-3 text-[11px] text-[#A3E635]">
          {r.converted_task_id && t("Aufgabe erstellt")} {r.converted_ticket_id && t("Supportfall erstellt")} {r.converted_design_id && t("Design-Idee erstellt")}
        </div>
      )}
    </Card>
  );
}

export default function AiRequests() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [loading, setLoading] = useState(false);

  const load = (f = filter, s = sortBy) => {
    setLoading(true);
    const qp = [];
    if (f) qp.push(`status=${f}`);
    if (s === "overdue") qp.push("sort=overdue");
    return api.get(`/admin/ai-requests${qp.length ? `?${qp.join("&")}` : ""}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(filter, sortBy); }, [filter, sortBy]); // eslint-disable-line
  if (!data) return <Spinner />;

  const s = data.stats || {};
  return (
    <div className="fade-in" data-testid="admin-ai-requests">
      <PageHeader title={t("KI-Wünsche zur Prüfung")} subtitle={t("Von Kunden gemeldete Wünsche (Text, Foto, Screenshot) — als Wunschkarte zusammengefasst. In Aufgabe oder Supportfall umwandeln.")}>
        <button onClick={() => load()} data-testid="airequests-refresh" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t("Aktualisieren")}
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile label={t("Wünsche gesamt")} value={s.total || 0} icon={Lightbulb} />
        <Tile label={t("Neu & Eingegangen")} value={(s.neu || 0) + (s.eingegangen || 0)} icon={Sparkles} />
        <Tile label={t("In Prüfung")} value={s.in_pruefung || 0} icon={CheckSquare} />
        <Tile label={t("Überfällig")} value={s.overdue || 0} icon={AlertTriangle} tone="red" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`airequests-filter-${f.key || "all"}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${filter === f.key ? "text-black border-transparent bg-[#A3E635]" : "text-gray-400 border-white/10 hover:text-white"}`}>
            {t(f.label)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 bg-[#121217] border border-white/10 rounded-lg p-1" data-testid="airequests-sort">
          {[["newest", t("Neueste")], ["overdue", t("Überfällig zuerst")]].map(([v, l]) => (
            <button key={v} onClick={() => setSortBy(v)} data-testid={`airequests-sort-${v}`}
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${sortBy === v ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              {v === "overdue" && <AlertTriangle className="w-3 h-3" />}{l}
            </button>
          ))}
        </div>
      </div>

      {(data.requests || []).length === 0 ? (
        <Empty text={t("Keine Wünsche in dieser Ansicht.")} hint={t("Sobald ein Kunde einen Wunsch über den KI-Assistenten sendet, erscheint er hier zur Prüfung.")} />
      ) : (
        <div className="space-y-4">
          {data.requests.map((r) => <RequestRow key={r.id} r={r} onChanged={() => load()} />)}
        </div>
      )}
    </div>
  );
}
